import { useQuery } from '@tanstack/vue-query'
import { Ref, computed } from 'vue'
import { useRouter } from 'vue-router'

import { DEFAULT_CATEGORY_PAGE_SIZE } from '@/constants/general'
import { useI18nAddon } from '@/i18n/i18n'
import { useAppStore } from '@/store/app-store'
import { ShortGameType } from '@/types'
import { useAPI } from '@/utils/requests/api-plugin'
import safeURLParse from '@/utils/safeURLParse'

type UseSearchPageDataParams = {
    searchQuery: Ref<string>
}

export const useSearchPageData = ({ searchQuery }: UseSearchPageDataParams) => {
    const appStore = useAppStore()
    const { currentLocale } = useI18nAddon()
    const { search } = useAPI()
    const router = useRouter()
    const i18nAddon = useI18nAddon()

    const searchQueryKey = computed(() => [
        'search-page-results',
        searchQuery.value,
        currentLocale.value,
        appStore.platform,
    ])

    type CategoryType = {
        href: string
        title: string
        id: string
    }
    const {
        data: searchData,
        suspense: searchSuspense,
        isPending: isLoading,
    } = useQuery({
        queryKey: searchQueryKey,
        queryFn: async () => {
            const result: {
                categories: CategoryType[]
                games: ShortGameType[]
                searchTerm: string
                isLoading: boolean
            } = {
                categories: [],
                games: [],
                searchTerm: searchQuery.value,
                isLoading: true,
            }

            if (!searchQuery.value || searchQuery.value.trim().length === 0) {
                return result
            }

            try {
                const searchResults = await search({
                    q: searchQuery.value,
                    num: String(DEFAULT_CATEGORY_PAGE_SIZE),
                    hl: i18nAddon.currentLocale.value,
                    lr: `lang_${i18nAddon.getCurrentLanguage()}`,
                })

                result.searchTerm = searchQuery.value

                result.categories =
                    (searchResults.items
                        ?.filter(
                            (item) =>
                                item.resultType === 'category' ||
                                item.resultType === 'tag' ||
                                item.resultType === 'series',
                        )
                        .map((item) => {
                            const linkPath = safeURLParse(item.link)?.pathname

                            if (!linkPath) {
                                return null
                            }

                            const route = router.resolve(linkPath)

                            return {
                                href: route.fullPath,
                                title: item.title,
                                id: '',
                            }
                        })
                        .filter(Boolean) as CategoryType[]) || []

                result.games =
                    searchResults.items
                        ?.filter((item) => item.resultType === 'game')
                        .map((item) => {
                            const shortGame: ShortGameType = {
                                hru: item.gameHru!,
                                preview: item.preview!,
                                icon: item.icon,
                                title: item.title,
                                videoId: item.videoId /*item.video_metadata?.[0]?.playgama_id,*/,
                                videoUrl: item.videoUrl /*item.videos?.[0]?.player_url,*/,
                                hasInAppPurchases: item.hasInAppPurchases,
                                badges: item.badges,
                                source: item.source || '',
                                platforms: item.platforms,
                            }
                            return shortGame
                        }) || []
            } catch (error) {
                console.error('Search error:', error)
            }

            return result
        },
        retry: 1,
    })

    const categories = computed<CategoryType[]>(() => searchData.value?.categories || [])
    const games = computed<ShortGameType[]>(() => searchData.value?.games || [])

    const preloadSearchPageData = async () => {
        await searchSuspense()
    }

    return {
        categories,
        games,
        isLoading,
        pageSize: DEFAULT_CATEGORY_PAGE_SIZE,
        preloadSearchPageData,
    }
}
